import React from "react";
import { motion } from "framer-motion";
import {
  Hand,
  Target,
  Activity,
  Brain,
  BarChart3,
  Zap,
} from "lucide-react";
import { useLang } from "../../context/LangContext";
import { translations } from "../../lib/i18n";
import { TEST_IDS } from "../../constants/testIds";

const ICONS = [Hand, Target, Activity, Brain, BarChart3, Zap];

const Methodology = () => {
  const { lang } = useLang();
  const t = translations[lang].methodology;

  return (
    <section
      id="methodology"
      data-testid={TEST_IDS.methodology.root}
      className="rp-section bg-[color:var(--rp-bg-2)]"
    >
      <div className="rp-container">
        <div className="grid grid-cols-12 gap-8 mb-16">
          <div className="col-span-12 lg:col-span-7">
            <div className="rp-eyebrow mb-4">06 · {t.eyebrow}</div>
            <h2 className="rp-heading rp-headline">{t.title}</h2>
          </div>
          <div className="col-span-12 lg:col-span-5 flex items-end">
            <p className="rp-lede">{t.intro}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
          {t.pillars.map((pillar, i) => {
            const Icon = ICONS[i] || Target;
            return (
              <motion.article
                key={pillar.n}
                data-testid={TEST_IDS.methodology.pillar(i)}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                className="group relative bg-[color:var(--rp-card)] p-8 md:p-10 hover:bg-[#1c2126] transition-colors duration-300 min-h-[300px] flex flex-col"
              >
                <div className="flex items-start justify-between mb-8">
                  <span className="font-display font-bold text-[color:var(--rp-accent)] text-sm tracking-widest">
                    {pillar.n}
                  </span>
                  <Icon
                    size={22}
                    strokeWidth={1.5}
                    className="text-white/60 group-hover:text-[color:var(--rp-accent)] transition-colors"
                  />
                </div>
                <h3 className="font-display font-bold text-white text-lg md:text-xl tracking-tight mb-5 leading-tight">
                  {pillar.title}
                </h3>
                <ul className="space-y-2 text-[13px] text-[color:var(--rp-muted)] leading-relaxed">
                  {pillar.items.map((it, k) => (
                    <li key={k} className="flex gap-2">
                      <span className="text-[color:var(--rp-accent)]/60 flex-shrink-0">·</span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                <div className="absolute bottom-0 left-0 h-[2px] w-0 group-hover:w-full bg-[color:var(--rp-accent)] transition-all duration-500" />
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Methodology;
